import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom';

const Sidebar = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const employmentStatus = localStorage.getItem('employmentStatus');
  const role = localStorage.getItem('role');
  const rawDesignationName = localStorage.getItem('designationName') || '';
  const normalizedDesignationName = rawDesignationName.replace(/\u00A0/g, ' ').trim();
  const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION || '';
  const hasAdminDesignation = adminDesignation && normalizedDesignationName.toLowerCase() === adminDesignation.trim().toLowerCase();
  const isPending = employmentStatus === 'pending';
  const isAdmin = role === 'admin';
  const isAdminLike = isAdmin || hasAdminDesignation;
  const isReportingManager = localStorage.getItem('isReportingManager') === 'true';
  const showUserDetails = isPending && !isAdminLike;

  // State to track open menus
  const [openMenus, setOpenMenus] = useState({
    onboarding: false,
    holiday: false,
    leave: false,
    attendance: false,
    organization: false,
    payroll: false,
    resignation: false
  });

  // Check if a path is active (exact match or specific routes)
  const isActive = (path) => {
    // Exact match for specific routes
    if (path === '/dashboard') {
      return pathname === '/dashboard';
    }
    if (path === '/employeeInvite') {
      return pathname === '/employeeInvite';
    }
    if (path === '/add-employee') {
      return pathname === '/add-employee';
    }
    if (path === '/employee') {
      return pathname === '/employee' || pathname.startsWith('/employeeview/') || pathname.startsWith('/employeeedit/');
    }
    if (path === '/holiday') {
      return pathname === '/holiday' || pathname.startsWith('/holiday-add') || pathname.startsWith('/holiday-edit/');
    }
    if (path === '/holiday-view') {
      return pathname === '/holiday-view';
    }
    if (path === '/leave-management') {
      return pathname === '/leave-management';
    }
    if (path === '/leave-apply') {
      return pathname === '/leave-apply';
    }
    if (path === '/leave-history') {
      return pathname === '/leave-history';
    }
    if (path === '/leave-balance') {
      return pathname === '/leave-balance';
    }
    if (path === '/work-location') {
      return pathname === '/work-location' || pathname.startsWith('/work-location/');
    }
    if (path === '/department') {
      return pathname === '/department';
    }
    if (path === '/designation') {
      return pathname === '/designation';
    }
    if (path === '/reporting-manager') {
      return pathname === '/reporting-manager';
    }
    if (path === '/store-assets') {
      return pathname === '/store-assets' ||
        pathname === '/store-assets-add' ||
        pathname.startsWith('/store-assets-edit/') ||
        pathname.startsWith('/asset-view/');
    }
    if (path === '/report') {
      return pathname === '/report' || (pathname.startsWith('/report/') && !pathname.startsWith('/reporting-manager'));
    }
    if (path === '/payroll-allocation') {
      return pathname === '/payroll-allocation' || pathname.startsWith('/payroll-allocation');
    }
    if (path === '/payslip') {
      return pathname === '/payslip' || (pathname.startsWith('/payslip') && !pathname.startsWith('/payslip-approval'));
    }
    if (path === '/payslip-approval') {
      return pathname === '/payslip-approval';
    }
    if (path === '/salary-structure') {
      return pathname === '/salary-structure';
    }
    if (path === '/apply-resignation') {
      return pathname === '/apply-resignation';
    }
    if (path === '/application-status') {
      return pathname === '/application-status';
    }
    if (path === '/application-approval') {
      return pathname === '/application-approval';
    }
    // For other routes, use startsWith
    return pathname.startsWith(path);
  };

  // Check if any onboarding child route is active
  const isOnboardingActive = pathname === '/employeeInvite' || pathname === '/add-employee' ||
    pathname === '/employee' || pathname.startsWith('/employeeview/') || pathname.startsWith('/employeeedit/');

  // Check if any leave management child route is active
  const isLeaveManagementActive = pathname === '/leave-management' || pathname === '/leave-apply' ||
    pathname === '/leave-history' || pathname === '/leave-balance';

  // Check if any organization child route is active
  const isOrganizationActive = pathname === '/work-location' || pathname.startsWith('/work-location') ||
    pathname === '/department' || pathname === '/designation' || pathname === '/reporting-manager' ||
    pathname === '/store-assets' || pathname === '/store-assets-add' ||
    pathname.startsWith('/store-assets-edit/') || pathname.startsWith('/asset-view/');

  // Check if any attendance child route is active
  const isAttendanceActive = pathname === '/timesheet-report' || pathname === '/report' ||
    pathname === '/attendance' || pathname === '/membership_servicebenefits' ||
    pathname.startsWith('/timesheet-report') ||
    (pathname.startsWith('/report/') && !pathname.startsWith('/reporting-manager'));

  // Check if any resignation child route is active
  const isResignationActive = pathname === '/apply-resignation' ||
    pathname === '/application-status' || pathname === '/application-approval' ||
    pathname.startsWith('/application-status-view/');

  // Check if any payroll child route is active
  const isPayrollActive = pathname === '/payroll-allocation' || pathname.startsWith('/payroll-allocation') ||
    pathname === '/payslip' || pathname.startsWith('/payslip') || pathname === '/payslip-approval' || pathname === '/salary-structure';

  // Check if any salary structure child route is active
  const isSalaryStructureActive = pathname === '/salary-structure';

    // Initialize menu states based on active routes
  useEffect(() => {
    setOpenMenus({
      onboarding: isOnboardingActive,
      holiday: (isAdminLike && isActive('/holiday')) || isActive('/holiday-view'),
      leave: isLeaveManagementActive,
      attendance: isAttendanceActive,
      organization: isOrganizationActive,
      resignation: isResignationActive,
      payroll: isPayrollActive,
      salaryStructure: isSalaryStructureActive
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isAdminLike]);

  // Toggle menu function
  const toggleMenu = (menuName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4">
      <div className="brand-link logo-switch">
        <img src="/assets/img/color icon_circle.png" alt="GeoHunt360" className="brand-image-xl logo-xs" />
        <img src="/assets/img/color icon_circle.png" alt="GeoHunt360" className="brand-image-xs logo-xl" />
      </div>
      <div className="sidebar">
        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" role="menu">
            {showUserDetails ? (
              <li className="nav-item">
                <Link to="/userdetails" className={`nav-link ${isActive('/userdetails') ? 'active' : ''}`}>
                  <i className="nav-icon fa-solid fa-user" />
                  <p>Users Details</p>
                </Link>
              </li>
            ) : (
              <>
                <li className="nav-item">
                  <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}>
                    <i className="nav-icon fa-solid fa-gauge-simple-high" />
                    <p>
                      Dashboard
                    </p>
                  </Link>
                </li>
                {isAdminLike && (
                  <li className={`nav-item ${openMenus.onboarding ? 'menu-open' : ''}`}>
                    <div
                      className={`nav-link ${isOnboardingActive ? 'active' : ''}`}
                      onClick={(e) => toggleMenu('onboarding', e)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="nav-icon fa-solid fa-users" />
                      <p>
                        Onboarding
                        <i className={`right fas ${openMenus.onboarding ? 'fa-angle-down' : 'fa-angle-left'}`} />
                      </p>
                    </div>
                    <ul className={`nav nav-treeview ${openMenus.onboarding ? 'menu-open' : ''}`}>
                      <li className="nav-item">
                        <Link to="/employeeInvite" className={`nav-link ${pathname === '/employeeInvite' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Invite Employee</p>
                        </Link>
                      </li>
                      {/* <li className="nav-item">
                        <Link to="/add-employee" className={`nav-link ${pathname === '/add-employee' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Add Employee</p>
                        </Link>
                      </li> */}
                      <li className="nav-item">
                        <Link to="/employee" className={`nav-link ${pathname === '/employee' || pathname.startsWith('/employeeview/') || pathname.startsWith('/employeeedit/') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Manage Employee</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}
                <li className={`nav-item ${openMenus.holiday ? 'menu-open' : ''}`}>
                  <div
                    className={`nav-link ${(isAdminLike && isActive('/holiday')) || isActive('/holiday-view') ? 'active' : ''}`}
                    onClick={(e) => toggleMenu('holiday', e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="nav-icon fa-solid fa-calendar-days" />
                    <p>
                      Holiday Management
                      <i className={`right fas ${openMenus.holiday ? 'fa-angle-down' : 'fa-angle-left'}`} />
                    </p>
                  </div>
                  <ul className={`nav nav-treeview ${openMenus.holiday ? 'menu-open' : ''}`}>
                    {isAdminLike && (
                      <li className="nav-item">
                        <Link to="/holiday" className={`nav-link ${pathname === '/holiday' || pathname.startsWith('/holiday-add') || pathname.startsWith('/holiday-edit/') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Manage Holiday</p>
                        </Link>
                      </li>
                    )}
                    <li className="nav-item">
                      <Link to="/holiday-view" className={`nav-link ${pathname === '/holiday-view' ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>View Holiday</p>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li className={`nav-item ${openMenus.leave ? 'menu-open' : ''}`}>
                  <div
                    className={`nav-link ${isLeaveManagementActive ? 'active' : ''}`}
                    onClick={(e) => toggleMenu('leave', e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i class="fa-solid fa-user-clock"></i>                    <p>
                      Leave/ Travel Management
                      <i className={`right fas ${openMenus.leave ? 'fa-angle-down' : 'fa-angle-left'}`} />
                    </p>
                  </div>
                  <ul className={`nav nav-treeview ${openMenus.leave ? 'menu-open' : ''}`}>
                    {(isAdminLike || isReportingManager) && (
                      <li className="nav-item">
                        <Link to="/leave-management" className={`nav-link ${pathname === '/leave-management' ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Approve Leaves/Travels</p>
                        </Link>
                      </li>
                    )}
                    <li className="nav-item">
                      <Link to="/leave-apply" className={`nav-link ${pathname === '/leave-apply' ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Apply Leave/Travel</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link to="/leave-history" className={`nav-link ${pathname === '/leave-history' ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Leave History</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link to="/leave-balance" className={`nav-link ${pathname === '/leave-balance' ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Leave Balance</p>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li className={`nav-item ${openMenus.attendance ? 'menu-open' : ''}`}>
                  <div
                    className={`nav-link ${isAttendanceActive ? 'active' : ''}`}
                    onClick={(e) => toggleMenu('attendance', e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="nav-icon fa-solid fa-id-card-clip" />
                    <p>
                      Attendance
                      <i className={`right fas ${openMenus.attendance ? 'fa-angle-down' : 'fa-angle-left'}`} />
                    </p>
                  </div>
                  <ul className={`nav nav-treeview ${openMenus.attendance ? 'menu-open' : ''}`}>
                    <li className="nav-item">
                      <Link to="/timesheet-report" className={`nav-link ${isActive('/timesheet-report') ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Timesheet Report</p>
                      </Link>
                    </li>
                    {!(isAdminLike || isReportingManager) && (
                      <li className="nav-item">
                        <Link to="/report" className={`nav-link ${isActive('/report') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Check-In/Out Report</p>
                        </Link>
                      </li>
                    )}
                    {(isAdminLike || isReportingManager) && (
                      <li className="nav-item">
                        <Link to="/attendance" className={`nav-link ${isActive('/attendance') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Attendance Report</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
                {isAdminLike && (
                  <li className={`nav-item ${openMenus.organization ? 'menu-open' : ''}`}>
                    <div
                      className={`nav-link ${isOrganizationActive ? 'active' : ''}`}
                      onClick={(e) => toggleMenu('organization', e)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="nav-icon fa-solid fa-building" />
                      <p>
                        Organization
                        <i className={`right fas ${openMenus.organization ? 'fa-angle-down' : 'fa-angle-left'}`} />
                      </p>
                    </div>
                    <ul className={`nav nav-treeview ${openMenus.organization ? 'menu-open' : ''}`}>
                      <li className="nav-item">
                        <Link to="/work-location" className={`nav-link ${isActive('/work-location') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Work Location</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/department" className={`nav-link ${isActive('/department') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Department</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/designation" className={`nav-link ${isActive('/designation') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Designation</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/reporting-manager" className={`nav-link ${isActive('/reporting-manager') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Reporting Manager</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/store-assets" className={`nav-link ${isActive('/store-assets') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Store Assets</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}
                <li className={`nav-item ${openMenus.payroll ? 'menu-open' : ''}`}>
                  <div
                    className={`nav-link ${isPayrollActive ? 'active' : ''}`}
                    onClick={(e) => toggleMenu('payroll', e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="nav-icon fa-solid fa-money-bill-wave" />
                    <p>
                      Payroll
                      <i className={`right fas ${openMenus.payroll ? 'fa-angle-down' : 'fa-angle-left'}`} />
                    </p>
                  </div>
                  <ul className={`nav nav-treeview ${openMenus.payroll ? 'menu-open' : ''}`}>
                    {isAdminLike && (
                      <>
                        <li className="nav-item">
                          <Link to="/payroll-allocation" className={`nav-link ${isActive('/payroll-allocation') ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon" />
                            <p>Payroll Allocation</p>
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link to="/payslip-approval" className={`nav-link ${isActive('/payslip-approval') ? 'active' : ''}`}>
                            <i className="far fa-circle nav-icon" />
                            <p>Payslip Approval</p>
                          </Link>
                        </li>
                      </>
                    )}
                    {!isAdminLike && (
                      <li className="nav-item">
                        <Link to="/payslip" className={`nav-link ${isActive('/payslip') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon" />
                          <p>Pay slip</p>
                        </Link>
                      </li>
                    )}
                    <li className="nav-item">
                      <Link to="/salary-structure" className={`nav-link ${isActive('/salary-structure') ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Salary Structure</p>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li className={`nav-item ${openMenus.resignation ? 'menu-open' : ''}`}>
                  <div
                    className={`nav-link ${isResignationActive ? 'active' : ''}`}
                    onClick={(e) => toggleMenu('resignation', e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="nav-icon fa-solid fa-file-signature" />
                    <p>
                      Resignation
                      <i className={`right fas ${openMenus.resignation ? 'fa-angle-down' : 'fa-angle-left'}`} />
                    </p>
                  </div>
                  <ul className={`nav nav-treeview ${openMenus.resignation ? 'menu-open' : ''}`}>
                    <li className="nav-item">
                      <Link to="/apply-resignation" className={`nav-link ${isActive('/apply-resignation') ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Apply Resignation</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link to="/application-status" className={`nav-link ${isActive('/application-status') ? 'active' : ''}`}>
                        <i className="far fa-circle nav-icon" />
                        <p>Application Status</p>
                      </Link>
                    </li>
                    
                  </ul>
                </li>
              </>
            )}
           <li className="nav-item">
              <Link
                to="/terms-and-conditions"
                className={`nav-link ${isActive("/terms-and-conditions") ? "active" : ""}`}
              >
                <i className="nav-icon fa-solid fa-file-contract" />
                <p>Terms and Conditions</p>
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  )
}

export default Sidebar